import axios from "axios";
import { backend_url } from "@/constants"; // Assuming your backend URL is stored here
import { AXIOS_CONFIG } from "../api";
import type {
  EngagementLetterStatus,
  EngagementLetterTemplate,
  EngagementLetterAttachment,
} from "@/types/engagement";

/**
 * Creates an engagement letter template for business owner calling it.
 * @param header The header of the template.
 * @param body The HTML content of the template as string;
 */
export const createEngagementLetterTemplate = async (
  header: string,
  body: string
): Promise<EngagementLetterTemplate> => {
  try {
    const response = await axios.post(
      `${backend_url}/api/engagement-letter/template/`,
      { header, body },
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data.content as EngagementLetterTemplate;
  } catch (error) {
    console.error("Error fetching business engagement letter template:", error);
    throw error;
  }
};
/**
 * Fetch engagement letter template for a business.
 *
 * @param {number} businessId - The ID of the business.
 * @returns {Promise<any>} The engagement letter template.
 */
export const getBusinessEngagementLetterTemplate = async (
  businessId: string
): Promise<EngagementLetterTemplate> => {
  try {
    const response = await axios.get(
      `${backend_url}/api/engagement-letter/template/business/${businessId}/`,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data.content as EngagementLetterTemplate;
  } catch (error) {
    console.error("Error fetching business engagement letter template:", error);
    throw error;
  }
};

/**
 * Sign an engagement letter for a specific tax year.
 *
 * @param {string} templateId - The ID of the template.
 * @param {string} signature - The user's signature.
 * @param {number} taxYear - The tax year.
 * @returns {Promise<EngagementLetterStatus>} The signed engagement letter status.
 */
export const signEngagementLetter = async (
  templateId: string,
  signature: string,
  taxYear: number
): Promise<EngagementLetterStatus> => {
  if (!taxYear) {
    throw new Error("Tax year is required for signing engagement letter");
  }

  try {
    const response = await axios.post(
      `${backend_url}/api/engagement-letter/template/${templateId}/sign/`,
      { signature, taxYear },
      {
        ...AXIOS_CONFIG,
      }
    );
    return response as any;
  } catch (error) {
    console.error("Error signing engagement letter:", error);
    throw error;
  }
};

/**
 * Get engagement letter status for a business for a specific tax year.
 *
 * @param {string} businessId - The ID of the business.
 * @param {number} taxYear - The tax year.
 * @returns {Promise<any>} The engagement letter status.
 */
export const getEngagementLetterStatus = async (
  businessId: string,
  taxYear: number
): Promise<EngagementLetterStatus> => {
  try {
    const response = await axios.get(
      `${backend_url}/api/engagement-letter/business/${businessId}/status/`,
      {
        ...AXIOS_CONFIG,
        params: { year: taxYear },
      }
    );
    return response.data.content as EngagementLetterStatus;
  } catch (error) {
    console.error("Error fetching engagement letter status:", error);
    throw error;
  }
};

/**
 * Upload an attachment to an engagement letter template.
 *
 * @param {string} templateId - The ID of the template.
 * @param {File} file - The PDF file to upload.
 * @returns {Promise<EngagementLetterAttachment>} The created attachment.
 */
export const uploadAttachment = async (
  templateId: string,
  file: File
): Promise<EngagementLetterAttachment> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(
      `${backend_url}/api/engagement-letter/template/${templateId}/attachments/`,
      formData,
      {
        ...AXIOS_CONFIG,
        headers: {
          ...AXIOS_CONFIG.headers,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data.content as EngagementLetterAttachment;
  } catch (error) {
    console.error("Error uploading attachment:", error);
    throw error;
  }
};

/**
 * List all attachments for an engagement letter template.
 *
 * @param {string} templateId - The ID of the template.
 * @returns {Promise<EngagementLetterAttachment[]>} List of attachments.
 */
export const listAttachments = async (
  templateId: string
): Promise<EngagementLetterAttachment[]> => {
  try {
    const response = await axios.get(
      `${backend_url}/api/engagement-letter/template/${templateId}/attachments/`,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data.content as EngagementLetterAttachment[];
  } catch (error) {
    console.error("Error listing attachments:", error);
    throw error;
  }
};

/**
 * Delete an attachment from an engagement letter template.
 *
 * @param {string} templateId - The ID of the template.
 * @param {string} attachmentId - The ID of the attachment to delete.
 * @returns {Promise<void>}
 */
export const deleteAttachment = async (
  templateId: string,
  attachmentId: string
): Promise<void> => {
  try {
    await axios.delete(
      `${backend_url}/api/engagement-letter/template/${templateId}/attachments/${attachmentId}/`,
      {
        ...AXIOS_CONFIG,
      }
    );
  } catch (error) {
    console.error("Error deleting attachment:", error);
    throw error;
  }
};

/**
 * Reorder attachments for an engagement letter template.
 *
 * @param {string} templateId - The ID of the template.
 * @param {string[]} attachmentIds - Array of attachment IDs in desired order.
 * @returns {Promise<void>}
 */
export const reorderAttachments = async (
  templateId: string,
  attachmentIds: string[]
): Promise<void> => {
  try {
    await axios.post(
      `${backend_url}/api/engagement-letter/template/${templateId}/attachments/reorder/`,
      { attachmentIds },
      {
        ...AXIOS_CONFIG,
      }
    );
  } catch (error) {
    console.error("Error reordering attachments:", error);
    throw error;
  }
};

/**
 * Generate preview PDF with attachments merged.
 *
 * @param {string} templateId - The ID of the template.
 * @param {object} options - Optional content to preview (if not provided, uses saved template).
 * @param {string} options.header - Optional header content (title).
 * @param {string} options.body - Optional body content (HTML).
 * @returns {Promise<{presignedUrl: string}>} Presigned URL to the preview PDF.
 */
export const getPreviewPdf = async (
  templateId: string,
  options?: { header?: string; body?: string }
): Promise<{ presignedUrl: string }> => {
  try {
    const response = await axios.post(
      `${backend_url}/api/engagement-letter/template/${templateId}/preview/`,
      {
        header: options?.header,
        body: options?.body,
      },
      {
        ...AXIOS_CONFIG,
      }
    );
    console.log("[getPreviewPdf] API response:", {
      status: response.status,
      data: response.data,
      content: response.data.content,
      sentHeader: options?.header ? "provided" : "not provided",
      sentBody: options?.body ? "provided" : "not provided",
    });
    return response.data.content as { presignedUrl: string };
  } catch (error) {
    console.error("Error generating preview:", error);
    throw error;
  }
};
